import { db } from "@/utils/firebase"
import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  collection,
  query,
  where,
  getDocs,
  FieldPath,
} from "firebase/firestore"
import { PORTFOLIOS_COLLECTION, USERS_COLLECTION } from "../constants"
import { Member, MemberPermission } from "@/types/schema"

/**
 * Update a member's information in all portfolios they belong to
 */
export const updateMemberInPortfolios = async (
  userId: string,
  data: Partial<Member>
) => {
  try {
    // Get user document to find their portfolios
    const userRef = doc(db, USERS_COLLECTION, userId)
    const userDoc = await getDoc(userRef)

    if (!userDoc.exists()) {
      console.log(`User ${userId} not found in Firestore`)
      return
    }

    const userData = userDoc.data()
    const portfolioIds = userData.portfolios || []

    // Update the member in each portfolio
    for (const portfolioId of portfolioIds) {
      const portfolioRef = doc(db, PORTFOLIOS_COLLECTION, portfolioId)
      const portfolioDoc = await getDoc(portfolioRef)

      if (!portfolioDoc.exists()) {
        console.log(`Portfolio ${portfolioId} not found in Firestore`)
        continue
      }

      const portfolio = portfolioDoc.data()
      const members = portfolio.members || []

      // Find the member in the portfolio
      const memberIndex = members.findIndex((m: Member) => m.userId === userId)

      if (memberIndex !== -1) {
        // Update the member
        const updatedMembers = [...members]
        updatedMembers[memberIndex] = {
          ...updatedMembers[memberIndex],
          ...data,
        }

        // Update the portfolio
        await updateDoc(portfolioRef, {
          members: updatedMembers,
        })

        console.log(`Updated member ${userId} in portfolio ${portfolioId}`)
      }
    }
  } catch (error) {
    console.error("Error updating member in portfolios:", error)
    throw error
  }
}

/**
 * Synchronize portfolio members from Firestore to local state
 */
export const syncPortfolioMembers = async (portfolioId: string) => {
  try {
    console.log(`Synchronizing members for portfolio ${portfolioId}`)

    // Get the portfolio document
    const portfolioRef = doc(db, PORTFOLIOS_COLLECTION, portfolioId)
    const portfolioDoc = await getDoc(portfolioRef)

    if (!portfolioDoc.exists()) {
      console.log(`Portfolio ${portfolioId} not found in Firestore`)
      return []
    }

    const portfolio = portfolioDoc.data()
    const members = portfolio.members || []

    console.log(`Found ${members.length} members in portfolio ${portfolioId}`)

    // Get full user data for each member
    const enrichedMembers: Member[] = []

    for (const member of members) {
      try {
        const userRef = doc(db, USERS_COLLECTION, member.userId)
        const userDoc = await getDoc(userRef)

        if (userDoc.exists()) {
          const userData = userDoc.data()

          // Merge user data with member data
          enrichedMembers.push({
            ...member,
            firstName: userData.firstName || member.firstName,
            lastName: userData.lastName || member.lastName,
            email: userData.email || member.email,
            phone: userData.phone || member.phone,
            avatarUrl: userData.avatarUrl || member.avatarUrl,
            avatarInitials: userData.avatarInitials || member.avatarInitials,
          })
        } else {
          // If user not found, just use member data
          enrichedMembers.push(member)
        }
      } catch (err) {
        console.error(
          `Error fetching user data for member ${member.userId}:`,
          err
        )
        enrichedMembers.push(member)
      }
    }

    return enrichedMembers
  } catch (error) {
    console.error("Error syncing portfolio members:", error)
    return []
  }
}

/**
 * Add a user to a portfolio
 */
export const addUserToPortfolio = async (
  userId: string,
  portfolioId: string,
  role: string,
  permissions: MemberPermission[],
  firstName?: string,
  lastName?: string,
  email?: string,
  phone?: string
) => {
  try {
    // Get portfolio document
    const portfolioRef = doc(db, PORTFOLIOS_COLLECTION, portfolioId)
    const portfolioDoc = await getDoc(portfolioRef)

    if (!portfolioDoc.exists()) {
      console.log(`Portfolio ${portfolioId} not found in Firestore`)
      return false
    }

    const portfolio = portfolioDoc.data()
    const members = portfolio.members || []
    const existingMember = members.some((m: Member) => m.email === email)

    // Create member object
    const member = {
      userId,
      role,
      permissions,
      status: "invited",
      firstName,
      lastName,
      email,
      phone,
    }

    // Add member to portfolio
    if (!existingMember) {
      await updateDoc(portfolioRef, {
        members: arrayUnion(member),
      })
    }

    // Get linked businesses for this portfolio and add user to them
    await addUserToLinkedBusinesses(
      userId,
      portfolioId,
      role,
      permissions,
      firstName,
      lastName,
      email,
      phone
    )

    console.log(`Added user ${userId} to portfolio ${portfolioId}`)
    return true
  } catch (error) {
    console.error("Error adding user to portfolio:", error)
    return false
  }
}

/**
 * Add a user to all businesses linked to a portfolio
 */
export const addUserToLinkedBusinesses = async (
  userId: string,
  portfolioId: string,
  role: string,
  permissions: MemberPermission[],
  firstName?: string,
  lastName?: string,
  email?: string,
  phone?: string
) => {
  try {
    // Get all businesses linked to this portfolio
    const businessesRef = collection(db, "businesses")
    const q = query(businessesRef, where("portfolioId", "==", portfolioId))
    const businessesSnapshot = await getDocs(q)

    if (businessesSnapshot.empty) {
      console.log(`No businesses found for portfolio ${portfolioId}`)
      return
    }

    // Add user to each business
    for (const businessDoc of businessesSnapshot.docs) {
      const businessId = businessDoc.id
      const businessRef = doc(db, "businesses", businessId)

      // Create member object for business
      const member = {
        userId,
        role,
        permissions,
        status: "active", // Active in business if they're in the portfolio
        addedAt: new Date().toISOString(),
        firstName,
        lastName,
        email,
        phone,
        addedViaPortfolio: portfolioId, // Track that this access came from portfolio
      }

      // Check if user is already a member of this business
      const businessData = businessDoc.data()
      const members = businessData.members || []
      const existingMember = members.find((m: Member) => m.userId === userId)

      if (!existingMember) {
        // Add member to business
        await updateDoc(businessRef, {
          members: arrayUnion(member),
        })

        console.log(
          `Added user ${userId} to business ${businessId} via portfolio ${portfolioId}`
        )
      } else {
        console.log(
          `User ${userId} is already a member of business ${businessId}`
        )
      }
    }
  } catch (error) {
    console.error("Error adding user to linked businesses:", error)
  }
}

/**
 * Remove a user from all businesses linked to a portfolio
 */
export const removeUserFromBusinesses = async (
  userId: string,
  portfolioId: string
) => {
  try {
    console.log(
      `Removing user ${userId} from businesses linked to portfolio ${portfolioId}`
    )

    // Get all businesses linked to this portfolio
    const businessesRef = collection(db, "businesses")
    const q = query(businessesRef, where("portfolioId", "==", portfolioId))
    const businessesSnapshot = await getDocs(q)

    if (businessesSnapshot.empty) {
      console.log(`No businesses found for portfolio ${portfolioId}`)
      return true
    }

    // Remove user from each business
    for (const businessDoc of businessesSnapshot.docs) {
      const businessId = businessDoc.id
      const businessRef = doc(db, "businesses", businessId)
      const businessData = businessDoc.data()
      const members = businessData.members || []

      // Find the member to remove
      const memberToRemove = members.find((m: Member) => m.userId === userId)

      if (memberToRemove) {
        // Remove member from business
        await updateDoc(businessRef, {
          members: arrayRemove(memberToRemove),
        })

        console.log(`Removed user ${userId} from business ${businessId}`)
      } else {
        console.log(`User ${userId} is not a member of business ${businessId}`)
      }
    }

    return true
  } catch (error) {
    console.error("Error removing user from businesses:", error)
    return false
  }
}

/**
 * Remove a user from a portfolio
 */
export const removeUserFromPortfolio = async (
  userId: string,
  portfolioId: string
) => {
  try {
    // Get portfolio document to find the member
    const portfolioRef = doc(db, PORTFOLIOS_COLLECTION, portfolioId)
    const portfolioDoc = await getDoc(portfolioRef)

    if (!portfolioDoc.exists()) {
      console.log(`Portfolio ${portfolioId} not found in Firestore`)
      return false
    }

    const portfolio = portfolioDoc.data()
    const members = portfolio.members || []

    // Find the member to remove
    const memberToRemove = members.find((m: Member) => m.userId === userId)

    if (!memberToRemove) {
      console.log(`User ${userId} is not a member of portfolio ${portfolioId}`)
      return false
    }

    // Remove member from portfolio
    await updateDoc(portfolioRef, {
      members: arrayRemove(memberToRemove),
    })

    // Update user document to remove this portfolio
    const userRef = doc(db, USERS_COLLECTION, userId)
    await updateDoc(userRef, {
      portfolios: arrayRemove(portfolioId),
    })

    console.log(`Removed user ${userId} from portfolio ${portfolioId}`)
    return true
  } catch (error) {
    console.error("Error removing user from portfolio:", error)
    return false
  }
}
